import { CartUpdatedEvent } from '@theme/events';

/**
 * Suppression d'un coffret entier : le bouton porte les keys de toutes les
 * lignes du groupe (_coffretId) — on les retire séquentiellement puis on
 * notifie le thème (compteur + re-rendu du drawer).
 */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-ref="coffret-group-remove"]');
  if (!button || button.disabled) return;

  button.disabled = true;
  button.classList.add('is-loading');
  const keys = (button.dataset.keys || '').split(',').filter(Boolean);
  const drawerSectionId = document.querySelector('cart-drawer')?.dataset.sectionId;

  try {
    let cart = null;
    for (let index = 0; index < keys.length; index++) {
      const isLast = index === keys.length - 1;
      cart = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: keys[index],
          quantity: 0,
          ...(isLast && drawerSectionId ? { sections: drawerSectionId } : {}),
        }),
      }).then((response) => response.json());
    }

    if (cart) {
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: cart.item_count, source: 'coffret-group', sections: cart.sections }),
      );
    }
    // Page /cart (pas de re-rendu par sections) : rechargement simple
    if (!drawerSectionId || window.location.pathname.includes('/cart')) {
      window.location.reload();
    }
  } catch {
    button.disabled = false;
    button.classList.remove('is-loading');
  }
});

/**
 * Remise coffret & commande groupée : au clic « Commander » avec un coffret ou
 * une commande groupée au panier (les deux rendent une carte .coffret-group),
 * on demande au backend un code usage-unique du montant exact cumulé
 * (validation serveur), puis on passe par /discount/CODE qui pose le cookie
 * avant le checkout. En cas d'échec (backend injoignable) : checkout plein
 * tarif — la vente d'abord, jamais de blocage.
 */
const COFFRET_DISCOUNT_ENDPOINT = `${
  localStorage.getItem('lfc-backend-override') || 'https://template.lesfrereschaussettes.com'
}/coffret/discount-code`;

let coffretCheckoutInFlight = false;

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest('[data-ref="checkout-button"]');
    if (!button || coffretCheckoutInFlight) return;
    if (!document.querySelector('.coffret-group')) return;

    event.preventDefault();
    event.stopPropagation();
    coffretCheckoutInFlight = true;
    button.classList.add('is-loading');

    // Code déjà rattaché au panier (retour du checkout) : le backend le
    // réutilise si le montant est inchangé, le désactive et le remplace sinon.
    const previousCode = document.querySelector('[data-ref="coffret-applied-code"]')?.dataset.code || null;

    const mint = (cartItems) =>
      fetch(COFFRET_DISCOUNT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          previousCode,
          items: cartItems.map((item) => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {},
          })),
        }),
      });

    (async () => {
      try {
        const cart = await fetch('/cart.js').then((response) => response.json());
        let response = await mint(cart.items).catch(() => null);
        if (!response?.ok) response = await mint(cart.items).catch(() => null);
        const data = response?.ok ? await response.json() : null;

        if (data?.code && data.code !== previousCode) {
          // Pose le cookie de remise sans navigation intermédiaire (le proxy
          // du thème en dev ne suit pas la redirection de /discount/CODE)
          await fetch(`/discount/${encodeURIComponent(data.code)}`, { credentials: 'same-origin' }).catch(() => {});
        }
      } catch {
        // backend injoignable : on continue sans code
      }
      window.location.href = '/checkout';
    })();
  },
  true,
);

/**
 * Modifier une commande groupée depuis le panier : le brouillon complet vit
 * côté backend (uuid _groupOrderId) — on le rouvre (statut draft), on retire
 * les lignes du panier et on renvoie vers le composer avec ?go=ID. Les lignes
 * inchangées gardent leur designId (pas de re-déclinaison au ré-ajout).
 */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-ref="group-order-edit"]');
  if (!button || button.disabled) return;

  button.disabled = true;
  const groupOrderId = button.dataset.groupOrderId;
  const base = localStorage.getItem('lfc-backend-override') || 'https://template.lesfrereschaussettes.com';

  try {
    const draft = await fetch(`${base}/group-order/${groupOrderId}`).then((response) => {
      if (!response.ok) throw new Error('brouillon introuvable');
      return response.json();
    });

    await fetch(`${base}/group-order/${groupOrderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: draft.state, status: 'draft' }),
    });

    const cart = await fetch('/cart.js').then((response) => response.json());
    const updates = {};
    for (const item of cart.items) {
      if (item.properties?._groupOrderId === groupOrderId) updates[item.key] = 0;
    }
    if (Object.keys(updates).length) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }

    window.location.href = `/pages/creer-ma-commande-groupee?go=${encodeURIComponent(groupOrderId)}`;
  } catch (error) {
    console.error('[group-order] réouverture impossible :', error);
    button.disabled = false;
    alert('Impossible de rouvrir la commande pour le moment — réessaie dans un instant.');
  }
});

/**
 * Modifier un coffret depuis le panier : reconstruit le brouillon du composer
 * à partir des lignes du groupe (_coffretId), puis ouvre le composer à l'étape
 * de composition. Au ré-ajout, le composer remplacera les anciennes lignes
 * (même coffretId).
 */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-ref="coffret-group-edit"]');
  if (!button || button.disabled) return;

  button.disabled = true;
  const coffretId = button.dataset.coffretId;

  try {
    const cart = await fetch('/cart.js').then((response) => response.json());
    const members = cart.items.filter((item) => item.properties?._coffretId === coffretId);
    const paires = members.filter((item) => item.properties._coffretRole === 'paire');
    const emballage = members.find((item) => item.properties._coffretRole === 'emballage');
    const message = members.find((item) => item.properties._coffretRole === 'message');
    if (!paires.length) throw new Error('coffret introuvable');

    const optionValue = (item, pattern) =>
      (item.options_with_values || []).find((option) => pattern.test(option.name))?.value || null;

    const slots = [];
    paires.forEach((item, index) => {
      for (let unit = 0; unit < item.quantity; unit++) {
        slots.push({
          slotId: `edit-${index}-${unit}`,
          productId: item.product_id,
          title: item.product_title,
          image: item.image,
          variantId: item.variant_id,
          sku: item.sku,
          price: item.price,
          taille: optionValue(item, /taille|pointure/i),
          gamme: optionValue(item, /gamme/i),
          designId: item.properties.designId || null,
          thumb: null,
        });
      }
    });

    const draft = {
      version: 1,
      coffretId,
      step: 'compose',
      capacity: parseInt(paires[0].properties._coffretCapacity, 10) || slots.length,
      slots,
      pending: null,
      packaging: emballage
        ? {
            type: (emballage.sku || '').indexOf('1-COFP') === 0 ? 'premium' : 'standard',
            variantId: emballage.variant_id,
            price: emballage.price,
          }
        : { type: null, variantId: null, price: 0 },
      message: message
        ? { enabled: true, text: message.properties.Message || '' }
        : { enabled: false, text: '' },
      updatedAt: Date.now(),
    };

    localStorage.setItem('lfc-coffret-draft:v1', JSON.stringify(draft));
    window.location.href = '/pages/compose-ton-coffret';
  } catch {
    button.disabled = false;
  }
});

/**
 * <coffret-thumb> — dans une carte coffret regroupée, remplace la photo produit
 * d'une paire personnalisée par la vignette de son design.
 */
class CoffretThumb extends HTMLElement {
  connectedCallback() {
    const designId = this.dataset.designId;
    const img = this.querySelector('img');
    if (!designId || !img || this.dataset.loaded) return;

    const editorBase = this.dataset.editorBase || 'https://template.lesfrereschaussettes.com';
    // Retry : erreurs transitoires ET vignettes de designs déclinés (commande
    // groupée) générées en différé côté serveur — elles arrivent en quelques
    // secondes, on repasse au lieu d'abandonner.
    const attempt = (remaining) => {
      fetch(`${editorBase}/design/thumbnails/${encodeURIComponent(designId)}`)
        .then((response) => (response.ok ? response.json() : []))
        .then((images) => {
          if (Array.isArray(images) && images[0]) {
            // TOUTES les faces du design, en rangée sous la ligne de la paire
            this.innerHTML = '';
            images.forEach((src) => {
              const face = document.createElement('img');
              face.src = src;
              face.alt = 'Aperçu de la personnalisation';
              face.loading = 'lazy';
              this.appendChild(face);
            });
            this.classList.add('is-loaded');
            this.dataset.loaded = 'true';
          } else if (remaining > 0) {
            setTimeout(() => attempt(remaining - 1), 2000);
          }
        })
        .catch(() => {
          if (remaining > 0) setTimeout(() => attempt(remaining - 1), 2000);
        });
    };
    attempt(4);
  }
}

if (!customElements.get('coffret-thumb')) {
  customElements.define('coffret-thumb', CoffretThumb);
}

/**
 * <cart-personalization> — enrichit une ligne de panier personnalisée :
 *  - charge les thumbnails du design (GET {editorBase}/design/thumbnails/{designId})
 *  - réaligne le paramètre `origin` des liens Modifier/Dupliquer sur l'origine
 *    réellement visitée (le Liquid rend le domaine canonique, faux en dev/preview)
 *
 * Custom element : se ré-initialise tout seul quand le drawer ou la page panier
 * re-rend ses lignes après une mise à jour du panier.
 */
class CartPersonalization extends HTMLElement {
  connectedCallback() {
    this.#realignOriginParams();
    this.#loadThumbnails();
  }

  #realignOriginParams() {
    this.querySelectorAll('[data-ref="cart-perso-link"]').forEach((link) => {
      try {
        const url = new URL(link.href);
        const origin = url.searchParams.get('origin');
        if (!origin) return;

        const originUrl = new URL(origin);
        url.searchParams.set('origin', window.location.origin + originUrl.pathname + originUrl.search);
        link.href = url.toString();
      } catch {
        // URL invalide : on garde le lien rendu en Liquid
      }
    });
  }

  #loadThumbnails() {
    const container = this.querySelector('[data-ref="cart-perso-thumbs"]');
    const designId = this.dataset.designId;
    const editorBase = this.dataset.editorBase;
    if (!container || !designId || !editorBase || this.dataset.loaded) return;

    // Retry : transitoires + vignettes différées (designs déclinés de commande
    // groupée) — quelques tentatives espacées avant de laisser les placeholders.
    const attempt = (remaining) => {
      fetch(`${editorBase}/design/thumbnails/${encodeURIComponent(designId)}`)
        .then((response) => {
          if (!response.ok) throw new Error(`thumbnails ${response.status}`);
          return response.json();
        })
        .then((images) => {
          if (!Array.isArray(images) || images.length === 0) {
            if (remaining > 0) setTimeout(() => attempt(remaining - 1), 2000);
            return;
          }
          container.innerHTML = '';
          images.forEach((src) => {
            const img = document.createElement('img');
            img.src = src;
            img.alt = 'Aperçu de la personnalisation';
            img.loading = 'lazy';
            container.appendChild(img);
          });
          this.dataset.loaded = 'true';
        })
        .catch(() => {
          if (remaining > 0) setTimeout(() => attempt(remaining - 1), 2000);
        });
    };
    attempt(4);
  }
}

if (!customElements.get('cart-personalization')) {
  customElements.define('cart-personalization', CartPersonalization);
}
